import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronRight, MessageCircle } from 'lucide-react';

import { useAppSelector } from '@/store/hooks';
import { selectUnreadInboxCount } from '@/store/engagement.slice';

/**
 * Communication page — reached from the Profilo "Settings" block. Groups the
 * inbox and chat entry points (moved out of the Profilo body).
 */
export function Communication() {
  const { t } = useTranslation('general');
  const unreadInbox = useAppSelector(selectUnreadInboxCount);

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
      <header className="sr-only">
        <h1>
          {t('communication', { defaultValue: 'Communication' })}
        </h1>
      </header>

      <section className="flex flex-col gap-1">
        <CommRow to="/inbox" icon={Bell} badge={unreadInbox}>
          {t('engagement.inbox_title', { defaultValue: 'Inbox' })}
        </CommRow>
        <CommRow to="/chat" icon={MessageCircle}>
          {t('chat', { defaultValue: 'Chat' })}
        </CommRow>
      </section>
    </div>
  );
}

interface CommRowProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  badge?: number;
}

function CommRow({ to, icon: Icon, children, badge }: CommRowProps) {
  return (
    <Link
      to={to}
      className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <Icon className="h-5 w-5 text-fg" />
      <span className="flex-1 text-sm font-medium text-fg">{children}</span>
      {badge != null && badge > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-brand-fg">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-fg-muted" />
    </Link>
  );
}
